import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { 
  Bell,
  Mail,
  BookOpen,
  Award,
  MessageSquare,
  Calendar,
  Save,
  Loader2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationSettings = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    training_reminders: true,
    certificate_expiry: true,
    toolbox_talk_assignments: true,
    compliance_deadlines: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notification-preferences`, { withCredentials: true });
      setPreferences(response.data);
    } catch (error) {
      console.error("Error fetching preferences:", error);
      toast.error("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/notification-preferences`, preferences, { withCredentials: true });
      toast.success("Notification preferences saved!");
    } catch (error) {
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const notificationOptions = [
    {
      key: "training_reminders",
      icon: BookOpen,
      title: "Training Reminders",
      description: "Receive reminders when training assignments are due or overdue",
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      key: "certificate_expiry",
      icon: Award,
      title: "Certificate Expiry Alerts",
      description: "Get notified when your certifications are about to expire",
      color: "text-amber-600",
      bgColor: "bg-amber-100"
    },
    {
      key: "toolbox_talk_assignments",
      icon: MessageSquare,
      title: "Toolbox Talk Assignments",
      description: "Receive notifications when new toolbox talks are assigned to you",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100"
    },
    {
      key: "compliance_deadlines",
      icon: Calendar,
      title: "Compliance Deadlines",
      description: "Get alerts for upcoming compliance deadlines and important dates",
      color: "text-violet-600",
      bgColor: "bg-violet-100"
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="notification-settings-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              Notification Preferences
            </h1>
            <p className="text-muted-foreground mt-1">
              Choose which email notifications you want to receive.
            </p>
          </div>
        </div>

        {/* Email Info */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-center gap-3">
            <Mail className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">Notifications will be sent to:</p>
              <p className="text-sm text-blue-700">{user?.email}</p>
            </div>
          </CardContent>
        </Card>

        {/* Notification Options */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Email Notifications</CardTitle>
            <CardDescription>
              Toggle notifications on or off. You can change these settings at any time.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {notificationOptions.map((option) => {
              const Icon = option.icon;
              return (
                <div 
                  key={option.key}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                  data-testid={`notification-${option.key}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${option.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${option.color}`} />
                    </div>
                    <div>
                      <Label htmlFor={option.key} className="font-medium cursor-pointer">
                        {option.title}
                      </Label>
                      <p className="text-sm text-muted-foreground">
                        {option.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={option.key}
                    checked={preferences[option.key]}
                    onCheckedChange={() => handleToggle(option.key)}
                    data-testid={`toggle-${option.key}`}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="save-preferences-btn">
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </div>

        {/* Note */}
        <p className="text-sm text-muted-foreground text-center">
          Note: Even with notifications disabled, you may still receive critical system alerts.
        </p>
      </div>
    </DashboardLayout>
  );
};

export default NotificationSettings;
